/**
 * Derives a full widget palette from one brand colour, per theme.
 * The maths is plain sRGB mixing - no colour library, the widget stays lean.
 * Granular colour knobs written after these vars override anything derived.
 */

interface Rgb {
  red: number;
  green: number;
  blue: number;
}

function parseHex(hex: string): Rgb | null {
  const normalized = hex.trim().replace("#", "");
  const expanded =
    normalized.length === 3
      ? normalized
          .split("")
          .map((ch) => ch + ch)
          .join("")
      : normalized;
  if (!/^[0-9a-fA-F]{6}$/.test(expanded)) return null;
  return {
    red: parseInt(expanded.slice(0, 2), 16),
    green: parseInt(expanded.slice(2, 4), 16),
    blue: parseInt(expanded.slice(4, 6), 16),
  };
}

function toHex(color: Rgb): string {
  const channel = (value: number) =>
    Math.round(Math.min(255, Math.max(0, value)))
      .toString(16)
      .padStart(2, "0");
  return `#${channel(color.red)}${channel(color.green)}${channel(color.blue)}`;
}

/** Mix `amount` of `into` over `base` (0 = base, 1 = into). */
function mix(base: Rgb, into: Rgb, amount: number): Rgb {
  return {
    red: base.red + (into.red - base.red) * amount,
    green: base.green + (into.green - base.green) * amount,
    blue: base.blue + (into.blue - base.blue) * amount,
  };
}

function alpha(color: Rgb, value: number): string {
  return `rgba(${Math.round(color.red)}, ${Math.round(color.green)}, ${Math.round(color.blue)}, ${value})`;
}

/** WCAG relative luminance. */
function relativeLuminance(color: Rgb): number {
  const linear = (value: number) => {
    const scaled = value / 255;
    return scaled <= 0.04045 ? scaled / 12.92 : ((scaled + 0.055) / 1.055) ** 2.4;
  };
  return (
    0.2126 * linear(color.red) +
    0.7152 * linear(color.green) +
    0.0722 * linear(color.blue)
  );
}

/** Picks a readable icon/text colour on the accent. */
function isLightColor(color: Rgb): boolean {
  return relativeLuminance(color) > 0.45;
}

/** WCAG contrast ratio, 1 (none) to 21 (black on white). */
function contrastRatio(a: Rgb, b: Rgb): number {
  const luminanceA = relativeLuminance(a);
  const luminanceB = relativeLuminance(b);
  const lighter = Math.max(luminanceA, luminanceB);
  const darker = Math.min(luminanceA, luminanceB);
  return (lighter + 0.05) / (darker + 0.05);
}

/** Parses hex or rgb()/rgba() - the forms CSS custom properties resolve to.
    Translucent rgba() returns null: without knowing the backdrop the
    composited colour is unknowable, and a wrong contrast is worse than none. */
function parseCssColor(value: string): Rgb | null {
  const trimmed = value.trim();
  if (trimmed.startsWith("#")) return parseHex(trimmed);
  const rgbMatch = trimmed.match(
    /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+)\s*)?\)$/,
  );
  if (!rgbMatch) return null;
  if (rgbMatch[4] !== undefined && parseFloat(rgbMatch[4]) < 1) return null;
  return {
    red: parseInt(rgbMatch[1], 10),
    green: parseInt(rgbMatch[2], 10),
    blue: parseInt(rgbMatch[3], 10),
  };
}

const WHITE: Rgb = { red: 255, green: 255, blue: 255 };
const INK: Rgb = { red: 28, green: 28, blue: 30 };

/**
 * Link colour that keeps the accent's hue but reads against the surface:
 * the accent unchanged when it already passes WCAG AA (4.5:1), otherwise
 * walked toward white or ink (whichever pole the surface can support)
 * until it passes. An accent equal to the surface comes out as a pale or
 * deep tint of itself, never invisible.
 *
 * Returns null when either colour fails to parse - the caller leaves the
 * CSS fallback (currentColor) in charge rather than guessing.
 */
export function readableLinkColor(
  accentValue: string,
  surfaceValue: string,
): string | null {
  const accent = parseCssColor(accentValue);
  const surface = parseCssColor(surfaceValue);
  if (!accent || !surface) return null;

  const TARGET = 4.5;
  if (contrastRatio(accent, surface) >= TARGET) return toHex(accent);

  const pole =
    contrastRatio(WHITE, surface) >= contrastRatio(INK, surface) ? WHITE : INK;
  for (let amount = 0.05; amount < 1; amount += 0.05) {
    const candidate = mix(accent, pole, amount);
    if (contrastRatio(candidate, surface) >= TARGET) return toHex(candidate);
  }
  // Even the pole misses 4.5:1 (mid-grey surfaces top out around 4.2) -
  // the pole is still the most readable colour available.
  return toHex(pole);
}
const NIGHT: Rgb = { red: 16, green: 18, blue: 20 };
const NIGHT_TEXT: Rgb = { red: 232, green: 234, blue: 237 };

export function deriveAccentPalette(
  accentHex: string,
  isDark: boolean,
): Record<string, string> {
  const accent = parseHex(accentHex);
  if (!accent) return {};

  if (!isDark) {
    const onAccent = isLightColor(accent) ? toHex(INK) : "#ffffff";
    return {
      "--primary-color": toHex(accent),
      "--send-bg": toHex(accent),
      "--send-icon-color": onAccent,
      "--bot-msg-bg": toHex(mix(accent, WHITE, 0.88)),
      "--bot-msg-color": toHex(mix(accent, INK, 0.8)),
      "--user-msg-bg": toHex(mix(accent, WHITE, 0.94)),
      "--user-msg-color": toHex(mix(accent, INK, 0.88)),
      "--widget-bg": "#ffffff",
      "--input-bg": toHex(mix(accent, WHITE, 0.96)),
      "--input-field-bg": "#ffffff",
      "--border-color": toHex(mix(accent, WHITE, 0.86)),
      "--input-border-color": toHex(mix(accent, WHITE, 0.82)),
      "--launcher-bg": toHex(mix(accent, NIGHT, 0.45)),
      "--launcher-icon-color": "#ffffff",
      "--gold": toHex(mix(accent, WHITE, 0.5)),
      "--gold-soft": toHex(mix(accent, INK, 0.15)),
      "--gold-ring": alpha(accent, 0.16),
      "--gold-tint": alpha(accent, 0.08),
      "--gold-edge": alpha(accent, 0.28),
      "--surface-raised": toHex(mix(accent, WHITE, 0.95)),
      "--surface-sunken": toHex(mix(accent, WHITE, 0.92)),
      "--hairline-soft": toHex(mix(accent, WHITE, 0.86)),
    };
  }

  const lifted = mix(accent, WHITE, 0.25);
  const onLifted = isLightColor(lifted) ? toHex(NIGHT) : "#ffffff";
  return {
    "--primary-color": toHex(lifted),
    "--send-bg": toHex(lifted),
    "--send-icon-color": onLifted,
    "--bot-msg-bg": toHex(mix(accent, NIGHT, 0.78)),
    "--bot-msg-color": toHex(mix(accent, NIGHT_TEXT, 0.85)),
    "--user-msg-bg": toHex(mix(accent, NIGHT, 0.7)),
    "--user-msg-color": toHex(mix(accent, NIGHT_TEXT, 0.9)),
    "--widget-bg": toHex(mix(accent, NIGHT, 0.93)),
    "--input-bg": toHex(mix(accent, NIGHT, 0.9)),
    "--input-field-bg": toHex(mix(accent, NIGHT, 0.95)),
    "--border-color": toHex(mix(accent, NIGHT, 0.82)),
    "--input-border-color": toHex(mix(accent, NIGHT, 0.78)),
    "--launcher-bg": toHex(mix(accent, NIGHT, 0.55)),
    "--launcher-icon-color": toHex(mix(accent, WHITE, 0.85)),
    "--gold": toHex(mix(accent, WHITE, 0.35)),
    "--gold-soft": toHex(mix(accent, WHITE, 0.15)),
    "--gold-ring": alpha(lifted, 0.2),
    "--gold-tint": alpha(lifted, 0.12),
    "--gold-edge": alpha(lifted, 0.3),
    "--surface-raised": toHex(mix(accent, NIGHT, 0.85)),
    "--surface-sunken": toHex(mix(accent, NIGHT, 0.9)),
    "--hairline-soft": toHex(mix(accent, NIGHT, 0.82)),
  };
}
